
/* ============================== src/components/FileManager.jsx ============================== */
import { useCallback, useMemo, useState } from "react";
import { Ic } from "./Icons";
import { ICONS } from "./iconsData";
import { SearchBox } from "./SearchBox";
import { fmtDMY, fmtDT } from "../utils/date";
import { formatSize } from "../utils/format";
import { groupByDocument } from "../utils/fileVersions";
import {
  getChildren,
  buildBreadcrumb,
  validateFolderName,
  getFolderPathString,
  flattenLatestDocuments,
} from "../utils/fileTree";
import {
  DIALOGS,
  TOASTS,
  FILE_ROOT_LABEL,
  UPLOAD_ACCEPT,
} from "../utils/constants";
import { useConfirm } from "../context/ConfirmContext";
import { useToast } from "../context/ToastContext";
import { copyToClipboard } from "../utils/clipboard";
import { fileShareUrl, folderShareUrl } from "../utils/fileLinks";
import FloatingMenu from "./FloatingMenu";

const COPY_TOASTS = Object.freeze({
  ok: "Ссылка скопирована",
  fail: "Не удалось скопировать ссылку",
});

const resolveInitialView = (
  highlightFileId,
  highlightFolderId,
  folders,
  files,
) => {
  if (highlightFileId) {
    const file = files.find((f) => f.id === highlightFileId);
    if (file) {
      return {
        folderId: file.folderId ?? null,
        showAll: false,
        searchQuery: "",
      };
    }
  }
  if (highlightFolderId) {
    const folder = folders.find((f) => f.id === highlightFolderId);
    if (folder) {
      return {
        folderId: folder.parentId ?? null,
        showAll: false,
        searchQuery: "",
      };
    }
  }
  return { folderId: null, showAll: false, searchQuery: "" };
};

const Breadcrumb = ({ items, onNavigate, onDropRoot }) => {
  const [isOver, setIsOver] = useState(false);

  return (
    <div
      className={`file-breadcrumb ${isOver ? "drag-over" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        setIsOver(true);
      }}
      onDragLeave={() => setIsOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsOver(false);
        onDropRoot(e);
      }}
    >
      <button type="button" className="link" onClick={() => onNavigate(null)}>
        {FILE_ROOT_LABEL}
      </button>{" "}
      {items.map((folder) => (
        <span key={folder.id} className="file-breadcrumb-item">
          <span className="file-breadcrumb-sep">/</span>{" "}
          <button
            type="button"
            className="link"
            onClick={() => onNavigate(folder.id)}
          >
            {folder.name}
          </button>{" "}
        </span>
      ))}
    </div>
  );
};

const FilePathButton = ({ folders, folderId, onNavigate }) => {
  const label = getFolderPathString(folders, folderId);
  return (
    <button
      type="button"
      className="file-path file-path-button"
      onClick={() => onNavigate(folderId || null)}
      title={`Перейти в папку: ${label}`}
    >
      {label}
    </button>
  );
};

const FolderRow = ({
  folder,
  canDelete,
  onOpen,
  onDelete,
  onCopyLink,
  highlighted,
  onDropItem,
  onMoveRequest,
}) => {
  const [isOver, setIsOver] = useState(false);

  const menuItems = [
    {
      id: "copy",
      label: "Скопировать ссылку",
      icon: ICONS.link,
      onClick: onCopyLink,
    },
    {
      id: "move",
      label: "Переместить",
      icon: ICONS.folder,
      onClick: onMoveRequest,
    },
    canDelete && { type: "divider" },
    canDelete && {
      id: "del",
      label: "Удалить папку",
      icon: ICONS.trash,
      danger: true,
      onClick: onDelete,
    },
  ].filter(Boolean);

  const cls = `file-item file-folder clickable-row${highlighted ? " file-folder--shared" : ""}${isOver ? " drag-over" : ""}`;

  return (
    <div
      className={cls}
      onClick={onOpen}
      draggable={true}
      onDragStart={(e) => {
        e.dataTransfer.setData(
          "application/json",
          JSON.stringify({ type: "folder", id: folder.id }),
        );
        e.dataTransfer.effectAllowed = "move";
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setIsOver(true);
      }}
      onDragLeave={() => setIsOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsOver(false);
        try {
          const data = JSON.parse(e.dataTransfer.getData("application/json"));
          if (data.id === folder.id) return;
          onDropItem(data.type, data.id, folder);
        } catch {
          /* ignore invalid JSON on drop */
        }
      }}
    >
      <Ic d={ICONS.folder} size={24} />
      <div className="file-info">
        <div className="file-name">{folder.name}</div>
        <div className="file-meta">Папка</div>
      </div>
      <FloatingMenu items={menuItems}>
        {({ buttonProps }) => (
          <button
            {...buttonProps}
            className="icon-btn"
            title="Действия"
            aria-label={`Действия с папкой ${folder.name}`}
          >
            <Ic d={ICONS.more} size={15} />
          </button>
        )}
      </FloatingMenu>
    </div>
  );
};

const DocumentRow = ({
  doc,
  onDelete,
  canDelete,
  employeeName,
  onCopyLink,
  highlighted,
  onRename,
  onMoveRequest,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { latest, versions } = doc;
  const hasHistory = versions.length > 1;

  const menuItems = [];
  menuItems.push({
    id: "copy",
    label: "Скопировать ссылку",
    icon: ICONS.link,
    onClick: () => onCopyLink(latest.id),
  });
  menuItems.push({
    id: "move",
    label: "Переместить",
    icon: ICONS.folder,
    onClick: onMoveRequest,
  });

  if (canDelete) {
    menuItems.push({
      id: "rename",
      label: "Переименовать",
      icon: ICONS.edit,
      onClick: onRename,
    });
  }
  if (hasHistory) {
    menuItems.push({ type: "divider" });
    menuItems.push({
      id: "history",
      label: expanded
        ? "Скрыть историю"
        : `История версий (${versions.length})`,
      icon: ICONS.archive,
      onClick: () => setExpanded((v) => !v),
    });
  }
  if (canDelete) {
    menuItems.push({ type: "divider" });
    menuItems.push({
      id: "del",
      label: "Удалить последнюю версию",
      icon: ICONS.trash,
      danger: true,
      onClick: () => onDelete(latest.id),
    });
  }

  const wrapperCls = `file-document${highlighted ? " file-document--shared" : ""}`;

  return (
    <div className={wrapperCls}>
      <div
        className="file-item"
        draggable={true}
        onDragStart={(e) => {
          e.dataTransfer.setData(
            "application/json",
            JSON.stringify({ type: "file", id: latest.id }),
          );
          e.dataTransfer.effectAllowed = "move";
        }}
      >
        <Ic d={ICONS.file} size={24} />
        <div className="file-info">
          <div className="file-name">
            {latest.name}
            {hasHistory && (
              <span className="file-version-badge">v{latest.version}</span>
            )}
          </div>
          <div className="file-meta">
            {formatSize(latest.size)} · загрузил{" "}
            {employeeName(latest.uploadedBy)}
            {latest.uploadedAt && ` ${fmtDMY(latest.uploadedAt)}`}
          </div>
        </div>

        <a href={latest.url} download={latest.name} className="btn ghost sm">
          Скачать
        </a>

        {menuItems.length > 0 && (
          <FloatingMenu items={menuItems}>
            {({ buttonProps }) => (
              <button
                {...buttonProps}
                className="icon-btn"
                title="Действия"
                aria-label={`Действия с файлом ${latest.name}`}
              >
                <Ic d={ICONS.more} size={15} />
              </button>
            )}
          </FloatingMenu>
        )}
      </div>
      {expanded && hasHistory && (
        <div className="file-versions">
          {versions.map((v, i) => (
            <div
              key={v.id}
              className={`file-version-row${i === 0 ? " current" : ""}`}
            >
              <span className="file-version-tag">v{v.version}</span>
              <span className="file-version-date">
                {v.uploadedAt ? fmtDT(v.uploadedAt) : "-"}
              </span>
              <span className="file-version-author">
                {employeeName(v.uploadedBy)}
              </span>
              <span className="file-version-size">{formatSize(v.size)}</span>
              <a href={v.url} download={v.name} className="link">
                Скачать
              </a>
              {canDelete && (
                <button
                  type="button"
                  className="icon-btn danger xs"
                  onClick={() => onDelete(v.id)}
                  title={`Удалить версию v${v.version}`}
                >
                  <Ic d={ICONS.trash} size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AllFilesList = ({
  folders,
  documents,
  canDelete,
  onDelete,
  employeeName,
  onNavigateToFolder,
  onCopyLink,
  highlightFileId = null,
  emptyMessage = "Файлов нет",
}) => {
  if (documents.length === 0)
    return <div className="mut sm">{emptyMessage}</div>;

  return (
    <div className="file-all-list">
      {documents.map((f) => {
        const highlighted = highlightFileId && f.id === highlightFileId;
        const cls = `file-item${highlighted ? " file-item--shared" : ""}`;
        return (
          <div key={f.id} className={cls}>
            <Ic d={ICONS.file} size={24} />
            <div className="file-info">
              <div className="file-name">
                {f.name}
                {f.version > 1 && (
                  <span className="file-version-badge">v{f.version}</span>
                )}
              </div>
              <div className="file-meta">
                <FilePathButton
                  folders={folders}
                  folderId={f.folderId}
                  onNavigate={onNavigateToFolder}
                />
                {" · "}
                {formatSize(f.size)} · загрузил {employeeName(f.uploadedBy)}
              </div>
            </div>
            <a href={f.url} download={f.name} className="btn ghost sm">
              Скачать
            </a>
            <button
              type="button"
              className="icon-btn"
              onClick={() => onCopyLink(f.id)}
              title="Скопировать ссылку"
              aria-label={`Скопировать ссылку на файл ${f.name}`}
            >
              <Ic d={ICONS.link} size={15} />
            </button>
            {canDelete && (
              <button
                type="button"
                className="icon-btn danger"
                onClick={() => onDelete(f.id)}
                title="Удалить"
              >
                <Ic d={ICONS.trash} size={14} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

const MoveDialog = ({ item, folders, onClose, onMove }) => {
  const [targetId, setTargetId] = useState(() => {
    if (item.type === "file") return item.data.folderId || "";
    return item.data.parentId || "";
  });

  const stopPropagation = useCallback((e) => e.stopPropagation(), []);

  const options = useMemo(() => {
    if (item.type === "file") {
      return [
        { value: "", label: "Корень" },
        ...folders.map((f) => ({
          value: f.id,
          label: getFolderPathString(folders, f.id),
        })),
      ].sort((a, b) => a.label.localeCompare(b.label));
    }

    const childrenMap = new Map();
    for (const f of folders) {
      if (!childrenMap.has(f.parentId)) childrenMap.set(f.parentId, []);
      childrenMap.get(f.parentId).push(f.id);
    }

    const excluded = new Set([item.data.id]);
    const stack = [item.data.id];
    while (stack.length) {
      const current = stack.pop();
      const children = childrenMap.get(current) || [];
      for (const child of children) {
        if (!excluded.has(child)) {
          excluded.add(child);
          stack.push(child);
        }
      }
    }

    return [
      { value: "", label: "Корень" },
      ...folders
        .filter((f) => !excluded.has(f.id))
        .map((f) => ({
          value: f.id,
          label: getFolderPathString(folders, f.id),
        })),
    ].sort((a, b) => a.label.localeCompare(b.label));
  }, [item, folders]);

  return (
    <div
      className="overlay move-dialog-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div className="modal move-dialog-box" onClick={stopPropagation}>
        <div className="modal-head">
          <h3>Переместить {item.type === "folder" ? "папку" : "файл"}</h3>
        </div>
        <div className="modal-body">
          <select
            className="inp"
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
          >
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="modal-foot">
          <div className="spacer" />
          <button className="btn ghost" onClick={onClose}>
            Отмена
          </button>
          <button
            className="btn primary"
            onClick={() => {
              const currentParent =
                item.type === "folder"
                  ? item.data.parentId
                  : item.data.folderId;
              if (targetId !== (currentParent || "")) {
                onMove(targetId || null);
              }
              onClose();
            }}
          >
            Переместить
          </button>
        </div>
      </div>
    </div>
  );
};

export const FileManager = ({
  files = [],
  folders = [],
  highlightFileId = null,
  highlightFolderId = null,
  onUpload,
  onDelete,
  onCreateFolder,
  onDeleteFolder,
  onMoveFile,
  onMoveFolder,
  onRenameFile,
  canUpload = true,
  canDelete = true,
  employeeName = (id) => id,
}) => {
  const { prompt, confirm } = useConfirm();
  const { showToast } = useToast();

  const [view, setView] = useState(() =>
    resolveInitialView(highlightFileId, highlightFolderId, folders, files),
  );
  const [history, setHistory] = useState([]);
  const [moveTarget, setMoveTarget] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const navigate = useCallback(
    (patch) => {
      setHistory((prev) => [...prev, view]);
      setView((prev) => ({ ...prev, ...patch }));
    },
    [view],
  );

  const goBack = useCallback(() => {
    if (history.length === 0) return;
    setView(history[history.length - 1]);
    setHistory((prev) => prev.slice(0, -1));
  }, [history]);

  const setSearchQuery = useCallback((q) => {
    setView((prev) => ({ ...prev, searchQuery: q }));
  }, []);

  const breadcrumb = useMemo(
    () => buildBreadcrumb(folders, view.folderId),
    [folders, view.folderId],
  );
  const { subfolders, subfiles } = useMemo(
    () => getChildren(folders, files, view.folderId),
    [folders, files, view.folderId],
  );
  const documents = useMemo(() => groupByDocument(subfiles), [subfiles]);
  const allDocuments = useMemo(
    () => flattenLatestDocuments(folders, files),
    [folders, files],
  );

  const normalizedQuery = view.searchQuery.trim().toLowerCase();
  const isSearching = normalizedQuery.length > 0;

  const searchResults = useMemo(() => {
    if (!isSearching) return [];
    return allDocuments.filter((f) =>
      f.name.toLowerCase().includes(normalizedQuery),
    );
  }, [allDocuments, isSearching, normalizedQuery]);

  const navigateToFolder = useCallback(
    (folderId) => {
      navigate({ folderId, showAll: false, searchQuery: "" });
    },
    [navigate],
  );

  const handleCopyFileLink = useCallback(
    async (fileId) => {
      const ok = await copyToClipboard(fileShareUrl(fileId));
      showToast(
        ok ? COPY_TOASTS.ok : COPY_TOASTS.fail,
        ok ? "success" : "warning",
      );
    },
    [showToast],
  );

  const handleCopyFolderLink = useCallback(
    async (folderId) => {
      const ok = await copyToClipboard(folderShareUrl(folderId));
      showToast(
        ok ? COPY_TOASTS.ok : COPY_TOASTS.fail,
        ok ? "success" : "warning",
      );
    },
    [showToast],
  );

  const handleCreateFolder = async () => {
    const name = await prompt(DIALOGS.createFolder);
    if (!name) return;

    const error = validateFolderName(name, subfolders, subfiles);
    if (error) {
      showToast(error, "error");
      return;
    }

    onCreateFolder(name.trim(), view.folderId);
  };

  const handleDeleteFolder = async (folder) => {
    const inside = getChildren(folders, files, folder.id);
    if (inside.subfolders.length > 0 || inside.subfiles.length > 0) {
      showToast(TOASTS.folderNotEmpty, "warning");
      return;
    }
    const ok = await confirm(DIALOGS.deleteFolder(folder.name));
    if (!ok) return;
    onDeleteFolder(folder.id);
  };

  const handleRenameFile = async (doc) => {
    const newName = await prompt({
      title: "Переименовать файл",
      message: "Введите новое имя файла",
      defaultValue: doc.latest.name,
      confirmLabel: "Переименовать",
    });
    if (!newName || newName === doc.latest.name) return;
    onRenameFile(doc.latest, newName);
  };

  const handleDropItem = (type, id, targetFolderOrRoot) => {
    if (type === "file") {
      const file = files.find((f) => f.id === id);
      if (file)
        onMoveFile(file, targetFolderOrRoot ? targetFolderOrRoot.id : null);
    } else if (type === "folder") {
      const folder = folders.find((f) => f.id === id);
      if (folder)
        onMoveFolder(folder, targetFolderOrRoot ? targetFolderOrRoot.id : null);
    }
  };

  const handleDropRoot = (e) => {
    try {
      const data = JSON.parse(e.dataTransfer.getData("application/json"));
      handleDropItem(data.type, data.id, null);
    } catch {
      /* ignore invalid JSON on drop */
    }
  };

  const handleFileChange = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (picked.length === 0) return;
    setIsUploading(true);
    try {
      await onUpload(picked, view.folderId);
    } finally {
      setIsUploading(false);
    }
  };

  const isEmpty = subfolders.length === 0 && documents.length === 0;
  const hasAnyFiles = files.length > 0;
  const canGoBack = history.length > 0;
  const viewMode = isSearching ? "search" : view.showAll ? "all" : "folder";

  return (
    <div className="tm-block">
      <div className="file-toolbar">
        <div className="file-toolbar-row">
          {canGoBack && (
            <button
              type="button"
              className="btn ghost sm"
              onClick={goBack}
              title="Вернуться к предыдущему виду"
            >
              <Ic d={ICONS.left} size={14} /> Назад
            </button>
          )}

          <SearchBox
            value={view.searchQuery}
            onChange={setSearchQuery}
            placeholder="Поиск файла…"
            className="file-search-box"
          />

          {viewMode === "folder" && hasAnyFiles && (
            <button
              type="button"
              className="btn ghost sm"
              onClick={() => navigate({ showAll: true })}
            >
              <Ic d={ICONS.list} size={13} /> Все файлы
            </button>
          )}

          {viewMode !== "search" && canUpload && (
            <>
              <button
                type="button"
                className="btn ghost sm"
                onClick={handleCreateFolder}
              >
                <Ic d={ICONS.folder} size={13} /> Создать папку
              </button>
              <input
                type="file"
                id="file-upload-input"
                className="file-input-hidden"
                onChange={handleFileChange}
                multiple
                accept={UPLOAD_ACCEPT}
              />
              <label
                htmlFor="file-upload-input"
                className={`btn primary sm${isUploading ? " is-disabled" : ""}`}
              >
                <Ic d={ICONS.file} size={14} />{" "}
                {isUploading ? "Загрузка…" : "Загрузить файлы"}
              </label>
            </>
          )}
        </div>

        <Breadcrumb
          items={breadcrumb}
          onNavigate={navigateToFolder}
          onDropRoot={handleDropRoot}
        />
      </div>

      {viewMode === "search" && (
        <AllFilesList
          folders={folders}
          documents={searchResults}
          canDelete={canDelete}
          onDelete={onDelete}
          employeeName={employeeName}
          onNavigateToFolder={navigateToFolder}
          onCopyLink={handleCopyFileLink}
          highlightFileId={highlightFileId}
          emptyMessage="Ничего не найдено"
        />
      )}

      {viewMode === "all" && (
        <AllFilesList
          folders={folders}
          documents={allDocuments}
          canDelete={canDelete}
          onDelete={onDelete}
          employeeName={employeeName}
          onNavigateToFolder={navigateToFolder}
          onCopyLink={handleCopyFileLink}
          highlightFileId={highlightFileId}
        />
      )}

      {viewMode === "folder" && (
        <>
          {isEmpty && <div className="mut sm">Папка пуста</div>}
          {subfolders.map((folder) => (
            <FolderRow
              key={folder.id}
              folder={folder}
              canDelete={canDelete}
              highlighted={folder.id === highlightFolderId}
              onOpen={() => navigateToFolder(folder.id)}
              onDelete={() => handleDeleteFolder(folder)}
              onCopyLink={() => handleCopyFolderLink(folder.id)}
              onDropItem={handleDropItem}
              onMoveRequest={() =>
                setMoveTarget({ type: "folder", data: folder })
              }
            />
          ))}
          {documents.map((doc) => {
            const highlighted =
              highlightFileId &&
              doc.versions.some((v) => v.id === highlightFileId);
            return (
              <DocumentRow
                key={doc.name}
                doc={doc}
                onDelete={onDelete}
                canDelete={canDelete}
                employeeName={employeeName}
                highlighted={highlighted}
                onCopyLink={handleCopyFileLink}
                onRename={() => handleRenameFile(doc)}
                onMoveRequest={() =>
                  setMoveTarget({ type: "file", data: doc.latest })
                }
              />
            );
          })}
        </>
      )}

      {moveTarget && (
        <MoveDialog
          item={moveTarget}
          folders={folders}
          onClose={() => setMoveTarget(null)}
          onMove={(targetId) => {
            if (moveTarget.type === "file") {
              onMoveFile(moveTarget.data, targetId);
            } else {
              onMoveFolder(moveTarget.data, targetId);
            }
          }}
        />
      )}
    </div>
  );
};
